import {
  DEFECT_STATUS_TRANSITIONS,
  type DefectListEntry,
  type DefectStatus,
  type SeverityLevel,
} from '@testflow/shared';
import type { DragEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IdLabel, KiGeneratedBadge, TestCaseTitleLine } from '../../components/atoms.js';
import { AvatarBubble, SeverityBadge, severityToneClass } from '../../components/board.js';
import { CardMenu } from '../../components/card-menu.js';
import type { ProjectMember } from '../../lib/plans-api.js';

// #43: Die Karte war ein einziger <button>. Sie traegt jetzt eigene Aktionsknoepfe (Status,
// Bearbeiter) — ein Knopf im Knopf ist ungueltiges HTML, und `draggable` auf einem <button>
// verhalten Browser uneinheitlich. Deshalb Container + Titelknopf fuer den Tastaturweg.
export function DefectCard({
  d,
  severity,
  onOpen,
  onTransition,
  onAssign,
  // Review-Fix Important 2: bewusst ohne No-op-Standard. Ein No-op-Default machte onDragStart
  // immer "vorhanden" (die Funktion selbst ist truthy) — draggable liesse sich darueber nicht
  // mehr sinnvoll gaten, und ein kuenftiger Aufrufer, der den Handler schlicht vergisst, erbte
  // eine ziehbare, aber wirkungslose Karte. Mit optionalem undefined bleibt "kein Handler" auch
  // zur Laufzeit sichtbar.
  onDragStart,
  onDragEnd,
  members,
  busy,
}: {
  d: DefectListEntry;
  severity: SeverityLevel | null;
  onOpen: () => void;
  onTransition: (to: DefectStatus) => void;
  onAssign: (assigneeId: string | null, assigneeName: string | null) => void;
  onDragStart?: (e: DragEvent<HTMLElement>) => void;
  onDragEnd?: () => void;
  members: ProjectMember[];
  busy: boolean;
}) {
  const { t } = useTranslation();
  const [menu, setMenu] = useState<'status' | 'assignee' | null>(null);
  const tone = severityToneClass(severity?.color_token);
  // Review-Fix Important 2: Waehrend busy bleiben beide Aktionsknoepfe disabled, aber ohne
  // optimistisches Verschieben steht die Karte sichtbar weiter in ihrer alten Spalte. Bliebe sie
  // ziehbar, koennte ein zweiter Drop denselben Uebergang ein zweites Mal anstossen — die zweite
  // Anfrage trifft dann einen Server, der den Zielstatus bereits hat, und wird abgelehnt. Fehlt
  // zusaetzlich ein onDragStart-Handler, ist Ziehen ohnehin wirkungslos.
  const draggableNow = Boolean(onDragStart) && !busy;

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Tastaturweg ist der Titel-Button, s. o.
    <article
      draggable={draggableNow}
      aria-busy={busy}
      onClick={onOpen}
      onDragStart={(e) => {
        // Firefox startet den Zug nur, wenn Nutzdaten gesetzt sind. Der Inhalt ist egal — den
        // gezogenen Defect fuehrt die Seite in ihrem Zustand, nicht der dataTransfer. Der Browser
        // loest dragstart nur aus, wenn draggable=true ist — onDragStart ist an dieser Stelle
        // also garantiert vorhanden.
        e.dataTransfer.setData('text/plain', d.defect_id);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart?.(e);
      }}
      onDragEnd={onDragEnd}
      className={`flex flex-col items-start gap-1 rounded-[8px] border border-[var(--border)] border-l-4 ${tone.edge} bg-bg-surface p-2 text-left shadow-sm hover:border-brand-accent ${busy ? 'opacity-50' : ''}`}
    >
      <div className="flex w-full items-center justify-between gap-2">
        <IdLabel id={d.human_id} />
        <SeverityBadge severity={severity} />
      </div>
      {/* Der Titel ist der Tastaturweg ins Panel. Kein eigener onClick: der Klick bubbelt zum
          onClick der <article> hoch (ebenso ein per Tastatur ausgeloester Klick auf den fokussierten
          Button) — ein zusaetzlicher Handler hier wuerde onOpen pro Klick zweimal feuern. Die Karte
          selbst ist kein Knopf mehr, damit die Aktionsknoepfe darin gueltiges HTML sind. */}
      <button type="button" className="text-left text-sm text-text-primary">
        {d.title}
      </button>
      {/* #204: Titel stammt von der KI; verschwindet, sobald jemand ihn von Hand aendert. */}
      {d.title_ai && <KiGeneratedBadge hint={t('defect.aiTitleHint')} />}
      {/* #188: Testfall-Titel als eigene Zeile unter dem Defect-Titel (gekürzt, line-clamp-2). */}
      <TestCaseTitleLine titleDe={d.test_case_title_de} titleEn={d.test_case_title_en} />
      <div className="flex w-full items-center justify-between gap-2 text-[11px] text-text-muted">
        <span>
          {d.test_case_human_id ?? '—'}
          {d.tcode ? ` · ${d.tcode}` : ''}
          {/* #208: Schritt kompakt hinter dem Tcode. */}
          {d.failed_step ? ` · S${d.failed_step.step_no}` : ''}
        </span>
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: kein eigenes Interaktionsziel, faengt nur
            die Klick-Weitergabe von Bearbeiter-Knopf und -Menue ab, s. Kommentar am Statuschip
            weiter unten. */}
        <span className="relative" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            // Review-Fix Important 1: bewusst NICHT `disabled`. CardMenu gibt beim Schliessen den
            // Fokus per opener.focus() zurueck (card-menu.tsx) — genau in diesem Moment wird
            // `busy` wahr, und focus() auf ein deaktiviertes Element tut nichts, der Fokus faellt
            // auf <body>. aria-disabled haelt den Knopf fokussierbar; die Wirkung wird stattdessen
            // im Handler abgewiesen.
            aria-disabled={busy}
            aria-label={t('defect.card.assigneeMenu')}
            aria-haspopup="menu"
            aria-expanded={menu === 'assignee'}
            onClick={() => {
              if (busy) return;
              setMenu(menu === 'assignee' ? null : 'assignee');
            }}
            className="rounded-full hover:ring-2 hover:ring-brand-accent aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
          >
            <AvatarBubble name={d.assignee_name} />
          </button>
          {menu === 'assignee' && (
            <CardMenu
              label={t('defect.card.assigneeMenu')}
              onClose={() => setMenu(null)}
              items={[
                {
                  key: '__none__',
                  label: t('defect.unassigned'),
                  onSelect: () => onAssign(null, null),
                },
                ...members.map((m) => ({
                  key: m.user_id,
                  label: m.name,
                  onSelect: () => onAssign(m.user_id, m.name),
                })),
              ]}
            />
          )}
        </span>
      </div>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: kein eigenes Interaktionsziel, faengt nur
          die Klick-Weitergabe von Statuschip und Menue ab. stopPropagation auf dem Container,
          nicht auf dem Button selbst: der Container ist die Grenze zum aeusseren <article>
          und dadurch die instruktivste Stelle fuer Wartende. */}
      <div
        className="relative flex w-full items-center justify-between gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          // Review-Fix Important 1: s. Kommentar am Bearbeiter-Knopf oben — derselbe Grund.
          aria-disabled={busy}
          aria-label={t('defect.card.statusMenu')}
          aria-haspopup="menu"
          aria-expanded={menu === 'status'}
          onClick={() => {
            if (busy) return;
            setMenu(menu === 'status' ? null : 'status');
          }}
          className="rounded-chip border border-[var(--border)] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-text-muted hover:border-brand-accent aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
        >
          {t(`defect.status.${d.status}`)}
        </button>
        {menu === 'status' && (
          <CardMenu
            label={t('defect.card.statusMenu')}
            onClose={() => setMenu(null)}
            items={DEFECT_STATUS_TRANSITIONS[d.status].map((to) => ({
              key: to,
              label: t(`defect.status.${to}`),
              onSelect: () => onTransition(to),
            }))}
          />
        )}
      </div>
      {/* #194: der Melder eines nicht blockierenden Defects kann die Behebung noch selbst prüfen.
          Der Text wird gekürzt (truncate), damit die Karte in der 360-px-Spalte nicht überläuft;
          `title` trägt den vollen Text. */}
      {d.awaiting_reporter_name && (
        <span
          title={t('defects.awaitingReporter', { name: d.awaiting_reporter_name })}
          className="max-w-full truncate rounded-chip border border-[var(--border)] px-1.5 py-0.5 text-[10px] text-status-warn"
        >
          {t('defects.awaitingReporter', { name: d.awaiting_reporter_name })}
        </span>
      )}
    </article>
  );
}
